/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import StateComponent from '../StateManager/StateComponent';
import StateEvent from '../StateManager/StateEvent';
import StateManager from '../StateManager/StateManager';
import CLog, { LogTypes } from '../Utility/ComponentLogger';
import Effect from './Effect';

/**
 * A special type of [[Effect]] that executes a sequence of [[Effect]]s in order
 * using an internal state machine.
 */
@cc._decorator.ccclass
export default class EffectSequence extends Effect
{
    /** @hidden */
    private _effects: Effect[] = [];

    public get EffectManager(): StateManager { return this.stateManager; }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        // Create the StateManager, will grab all StateComponents from the children.
        this.stateManager = this.node.addComponent(StateManager);
        this.stateManager.OnComplete(() => this.EndSequence());
    }

    /** @hidden */
    protected start(): void
    {
        this.configureStateManager();
        super.start();
    }

    /**
     * Resets each [[Effect]] in the sequence and prepares the sequence to be triggered again.
     */
    public ResetSequence = (): void =>
    {
        this.enabled = true;
        this.Reset();
        this._effects.forEach(effect => effect.Reset());
    }

    /**
     * Initializes and configures the internal StateManager.
     * @hidden
     */
    private configureStateManager(): void
    {
        let initialState: StateComponent = this.stateManager.States[1];

        if (initialState === null)
        {
            CLog.error("No initial effect found.", this, LogTypes.Effect);
            return;
        }

        let states: StateComponent[] = this.stateManager.States
            .filter((x) => x.node.parent === this.node && x.node.active);

        this.stateManager.States.length = 0;
        this.stateManager.States.push(...states);

        this.stateManager.InitialState = initialState;

        if (!initialState || initialState.NextState !== null) { return; }

        let previousState: StateComponent = null;
        for (let stateComponent of this.stateManager.States)
        {
            // Assign the StateManager to each of the child StateComponents.
            stateComponent.StateManager = this.stateManager;

            // Fill next if not set
            if (previousState) { previousState.NextState = stateComponent; }
            previousState = stateComponent;
        }
    }

    /** Begins plaing the sequence of [[Effect]]s. */
    public Trigger = (): void =>
    {
        if (this.isComplete) { this.ResetSequence(); }

        this.trigger();
    }

    /** @hidden */
    protected trigger = (): void =>
    {
        if (!this.node.active)
        {
            this.complete();
            return;
        }

        this.stateManager.StartPlay();
    }

    /** Stops the current [[Effect]] and prevents further [[Effect]]s in the sequence from triggering. */
    public Stop(): void
    {
        for (let effect of this._effects)
        {
            if (effect.IsCurrentState())
            {
                effect.Stop();
            }
        }
    }

    public OnComplete(fnOnComplete: (args?: any) => any): void
    {
        this.stateManager.OnComplete(fnOnComplete);
    }

    public OffComplete(fnOnComplete: (args?: any) => any): void
    {
        this.stateManager.OffComplete(fnOnComplete);
    }

    /**
     * For triggering the sequence off of a state change.
     * @hidden
     */
    protected onStateChangeHandler = (event: StateEvent): void =>
    {
        this.trigger();
    }

    /**
     * Setting the complete flag when the internal StateManager has finished.
     * @hidden
     */
    private EndSequence = (): void =>
    {
        this.isComplete = true;
    }
}
